import { Avatar, Box, Chip, Typography } from '@mui/material';
import React from 'react';

import { DetectedFace } from '../uploadsApi';

const percent = (value: number) => `${Math.round(value * 100)}%`;

/** Who the picture is taken to be of, by status: what was found, and how sure each step was. */
const describeAssignment = (face: DetectedFace): { label: string; color: 'success' | 'info' | 'default' } => {
    if (face.assignment === 'confirmed' || face.assignment === 'manual') {
        return { label: 'Confirmed', color: 'success' };
    }
    if (face.assignment === 'suggested') {
        return { label: 'Suggested', color: 'info' };
    }
    return { label: 'Not identified', color: 'default' };
};

/** What the list is called: people, pets, or both. */
export const facesHeading = (faces: DetectedFace[]): string => {
    const pets = faces.filter((face) => face.kind === 'pet').length;
    if (pets === 0) {
        return 'People';
    }
    return pets === faces.length ? 'Pets' : 'People and pets';
};

const speciesLabel = (face: DetectedFace): string => face.species ?? 'pet';

/** The faces and animals found in a picture: each with its crop, who it is taken to be, and the scores behind that. */
export const FacesPanel: React.FC<{ faces: DetectedFace[]; ready: boolean }> = ({ faces, ready }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
            {facesHeading(faces)}
        </Typography>
        {faces.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
                {ready ? 'No faces found in this picture.' : 'Faces are looked for once the picture has been read.'}
            </Typography>
        ) : (
            <Box
                component="ul"
                aria-label={faces.some((face) => face.kind === 'pet') ? 'Faces and pets found' : 'Faces found'} sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1 }}>
                {faces.map((face) => {
                    const { label, color } = describeAssignment(face);
                    return (
                        <Box
                            component="li"
                            key={face.id}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
                        >
                            <Avatar
                                src={face.avatar ?? undefined}
                                alt={face.person_name ?? 'Unidentified face'}
                                sx={{ width: 48, height: 48 }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2">
                                        {face.person_name
                                            ? `${face.person_name}${face.is_user ? ' (the user)' : ''}`
                                            : face.kind === 'pet'
                                              ? `Unknown ${speciesLabel(face)}`
                                              : 'Nobody known'}
                                    </Typography>
                                    {face.kind === 'pet' && (
                                        <Chip size="small" variant="outlined" label={`Pet · ${speciesLabel(face)}`} />
                                    )}
                                    <Chip size="small" color={color} variant="outlined" label={label} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {face.assignment === 'manual' ? (
                                        'Tagged by the user; no face was found for them in this picture'
                                    ) : (
                                        <>
                                            {face.match_score !== null ? `Match ${percent(face.match_score)} · ` : ''}
                                            Face detected {percent(face.det_score as number)} · {face.model}
                                        </>
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        )}
    </Box>
);
